/* ============================================================================
   Arlo & Co — Name Labels personaliser (storefront engine)
   ----------------------------------------------------------------------------
   Rendered by sections/arlo-label-personaliser.liquid for label design
   products (template: product.label-design). Settings come from
   assets/arlo-label-config.js (window.ArloLabelConfig), generated and
   deployed by the admin's Labels page.

   The preview and the production PDFs share ONE canvas renderer (ported from
   the admin — keep in sync with admin/public/index.html), so what the
   customer sees is exactly what production prints:
     • white "sticker plate" outline via exact Euclidean-distance morphology
     • per-font scale / offset / line-space tuning
     • bag tags: first name only, bottom-anchored, laser template PDF
   Add to Cart:
     1. renders per-material sheet PDFs (stick-on; iron-on MIRRORED for heat
        transfer; one single-page PDF per sheet page, like multi-image decals)
        and the bag tag PDF on the laser template,
     2. uploads each to Uploadcare,
     3. adds pack variant + linked iron-on/bag-tag ($0) variants to the cart
        as separate line items with numbered _label_pdf_N property URLs.
   ========================================================================== */
(function () {
  'use strict';

  const root = document.getElementById('arlo-lp');
  if (!root) return;
  const cfg = window.ArloLabelConfig;
  if (!cfg) { console.error('[ArloLabels] window.ArloLabelConfig missing — deploy from the admin Labels page'); return; }

  let data;
  try {
    data = JSON.parse(document.getElementById('arlo-lp-data').textContent);
  } catch (e) {
    console.error('[ArloLabels] product data parse failed', e);
    return;
  }

  const ASSETS = {
    fontVic: root.dataset.fontVic || '',
    fontSpiffy: root.dataset.fontSpiffy || '',
    bagtagTpl: root.dataset.bagtagTemplate || '',
  };

  /* ── Shape + template constants (keep in sync with the admin) ─────────── */
  const LBL_SHAPES = {
    rect:   { label: 'Rectangle', wMm: 45, hMm: 24, aspect: 1.875,   previewW: 336, threshold: 8,  cqw: 19,   minRem: 3.0, maxRem: 6.5,  stroke: 10, size: '45 mm × 24 mm' },
    circle: { label: 'Large Round',    wMm: 40, hMm: 40, aspect: 1,       previewW: 340, threshold: 6,  cqw: 19.4, minRem: 4.5, maxRem: 7.25, stroke: 14, size: '⌀ 40 mm' },
    pencil: { label: 'Pencil',    wMm: 45, hMm: 10, aspect: 4.5,     previewW: 420, threshold: 16, cqw: 8.0,  minRem: 2.5, maxRem: 5.0,  stroke: 10, size: '45 mm × 10 mm' },
    // identical to pencil; separate type so packs can name it differently
    mini:   { label: 'Mini',      wMm: 45, hMm: 10, aspect: 4.5,     previewW: 420, threshold: 16, cqw: 8.0,  minRem: 2.5, maxRem: 5.0,  stroke: 10, size: '45 mm × 10 mm' },
    bagtag: { label: 'Bag Tag',   wMm: 68, hMm: 90, aspect: 68 / 90, previewW: 240, threshold: 8,  cqw: 16,   minRem: 2.5, maxRem: 6.0,  stroke: 10, size: '68 mm × 90 mm' },
  };
  const LBL_BAGTAG_TPL = {
    art: { x: 38.034, y: 48.8484, w: 190.772, h: 253.134, cornerR: 8.9 },
    hole: [
      [133.478, 289.530],
      [141.430, 289.530, 147.877, 286.985, 147.877, 282.443],
      [147.877, 277.902, 141.430, 275.357, 133.478, 275.357],
      [125.525, 275.357, 119.078, 277.902, 119.078, 282.443],
      [119.078, 286.985, 125.525, 289.530, 133.478, 289.530],
    ],
    content: { x0: 30.8052, y0: 14.2124, x1: 236.6299, y1: 302.9743 },
    pageMarginMm: 5,
    cutW: 192.756,
  };
  // Standalone mode: this product IS a single label item (iron-on sheet, bag
  // tag …) rather than a design sold in packs. Same builder, no pack selector
  // and no add-on cards — the customer's chosen variant is what's added.
  //   1. the section's "Builder profile" setting (set per template), then
  //   2. a "label-profile:<name>" product tag, then
  //   3. the legacy per-handle map — first match wins.
  const STANDALONE = (function resolveProfile() {
    const profiles = cfg.builderProfiles || {};
    const fromSetting = (root.dataset.builderProfile || '').trim();
    if (fromSetting && profiles[fromSetting]) return profiles[fromSetting];
    const tags = (root.dataset.productTags || '').split(',').map((t) => t.trim());
    for (const t of tags) {
      const m = /^label-profile:(.+)$/i.exec(t);
      if (m && profiles[m[1].trim()]) return profiles[m[1].trim()];
    }
    return (cfg.standaloneProducts || {})[data.handle] || null;
  })();
  // Classic (outlined) design for this product — admin Labels → Classic labels.
  const CLASSIC = (cfg.classicDesigns || []).find((d) => (d.productHandles || []).indexOf(data.handle) !== -1) || null;
  const PREVIEW_SHAPES = STANDALONE
    ? (STANDALONE.previewShapes && STANDALONE.previewShapes.length ? STANDALONE.previewShapes : ['rect'])
    : ['rect', 'circle', 'pencil', 'bagtag'];

  /* ── Fonts: inject @font-face for the configured set ──────────────────── */
  (function injectFonts() {
    const BUILTIN_LOCAL = {
      'Vic Modern Cursive': { url: ASSETS.fontVic, fmt: 'truetype' },
      'Spiffy McGee': { url: ASSETS.fontSpiffy, fmt: 'opentype' },
    };
    const GOOGLE = { 'Fredoka One': 'Fredoka+One', 'Nunito': 'Nunito:wght@700;800' };
    const googleFams = [];
    let css = '';
    (cfg.fonts || []).forEach((f) => {
      if (f.builtin && GOOGLE[f.family]) {
        googleFams.push(GOOGLE[f.family]);
      } else if (f.builtin && BUILTIN_LOCAL[f.family] && BUILTIN_LOCAL[f.family].url) {
        const b = BUILTIN_LOCAL[f.family];
        css += "@font-face{font-family:'" + f.family + "';src:url('" + b.url + "') format('" + b.fmt + "');font-display:swap;}\n";
      } else if (f.files && f.files.length) {
        const srcs = f.files.map((file) => "url('" + file.url + "') format('" + (file.format || 'woff') + "')").join(', ');
        css += "@font-face{font-family:'" + f.family + "';src:" + srcs + ";font-display:swap;}\n";
      }
    });
    if (googleFams.length) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=' + googleFams.join('&family=') + '&display=swap';
      document.head.appendChild(link);
    }
    if (css) {
      const st = document.createElement('style');
      st.textContent = css;
      document.head.appendChild(st);
    }
  })();

  /* ── Shared renderer (ported from the admin — keep in sync) ───────────── */
  function fitScale(text, threshold) {
    const len = (text || '').length;
    if (len <= threshold) return 1;
    return Math.max(0.45, threshold / len);
  }

  function fontPx(shapeKey, scale, labelW, twoLines, tallFont) {
    const s = LBL_SHAPES[shapeKey];
    let cqw = s.cqw;
    if (shapeKey === 'rect' && twoLines && tallFont) cqw = 13.0;
    const px = Math.min(s.maxRem * 16, Math.max(s.minRem * 16, (cqw / 100) * labelW));
    return px * scale;
  }

  function shapePath(ctx, w, h, shape) {
    ctx.beginPath();
    if (shape === 'circle') {
      ctx.arc(w / 2, h / 2, Math.min(w, h) / 2, 0, Math.PI * 2);
    } else if (shape === 'bagtag') {
      const R = w / 2;
      const rb = (9.105 / LBL_BAGTAG_TPL.cutW) * w;
      ctx.moveTo(0, R);
      ctx.arc(w / 2, R, R, Math.PI, 0);
      ctx.lineTo(w, h - rb);
      ctx.arcTo(w, h, w - rb, h, rb);
      ctx.lineTo(rb, h);
      ctx.arcTo(0, h, 0, h - rb, rb);
      ctx.closePath();
      const hcx = w * 0.5003;
      const hcy = h * 0.0805;
      ctx.moveTo(hcx + (14.4 / 192.756) * w, hcy);
      ctx.ellipse(hcx, hcy, (14.4 / 192.756) * w, (7.09 / 255.118) * h, 0, 0, Math.PI * 2);
      ctx.closePath();
    } else {
      const r = (shape === 'pencil' || shape === 'mini') ? h / 2 : (22 / 336) * w;
      ctx.moveTo(r, 0);
      ctx.arcTo(w, 0, w, h, r);
      ctx.arcTo(w, h, 0, h, r);
      ctx.arcTo(0, h, 0, 0, r);
      ctx.arcTo(0, 0, w, 0, r);
      ctx.closePath();
    }
  }

  // ── Classic (outlined) labels, 2026-10-29 ───────────────────────────────
  // No artwork: the whole canvas (bleed included) is the outline colour, and
  // the label shape inset by the outline thickness — measured from the CUT
  // line — is filled white. A slightly off cut just shows more outline, and
  // the sheet background takes the same colour.
  // cl: { outline, fill, outlineMm, outlineSmallMm }
  function classicOutlinePx(shapeKey, pxPerMm, cl) {
    const small = shapeKey === 'pencil' || shapeKey === 'mini';
    const mm = small ? (cl.outlineSmallMm != null && cl.outlineSmallMm !== '' ? +cl.outlineSmallMm : 1)
                     : (cl.outlineMm != null && cl.outlineMm !== '' ? +cl.outlineMm : 2);
    return Math.max(0, mm) * pxPerMm;
  }
  function drawClassicLabel(ctx, w, h, shapeKey, bleedPx, pxPerMm, cl, bagCornerFrac) {
    const t = classicOutlinePx(shapeKey, pxPerMm, cl);
    const lw = w - 2 * bleedPx, lh = h - 2 * bleedPx;      // label at the cut line
    const x0 = bleedPx + t, y0 = bleedPx + t, iw = lw - 2 * t, ih = lh - 2 * t;
    ctx.save();
    ctx.fillStyle = cl.outline || '#6ec1e4';
    ctx.fillRect(0, 0, w, h);
    if (iw > 0 && ih > 0) {
      ctx.beginPath();
      if (shapeKey === 'circle') {
        ctx.arc(w / 2, h / 2, Math.min(iw, ih) / 2, 0, Math.PI * 2);
      } else if (shapeKey === 'bagtag') {
        const R = iw / 2;
        const rb = Math.max(0, bagCornerFrac * lw - t);
        ctx.moveTo(x0, y0 + R);
        ctx.arc(x0 + iw / 2, y0 + R, R, Math.PI, 0);
        ctx.lineTo(x0 + iw, y0 + ih - rb);
        ctx.arcTo(x0 + iw, y0 + ih, x0 + iw - rb, y0 + ih, rb);
        ctx.lineTo(x0 + rb, y0 + ih);
        ctx.arcTo(x0, y0 + ih, x0, y0 + ih - rb, rb);
        ctx.closePath();
      } else {
        const rOuter = (shapeKey === 'pencil' || shapeKey === 'mini') ? lh / 2 : (22 / 336) * lw;
        const r = Math.max(0, Math.min(rOuter - t, iw / 2, ih / 2));
        ctx.moveTo(x0 + r, y0);
        ctx.arcTo(x0 + iw, y0, x0 + iw, y0 + ih, r);
        ctx.arcTo(x0 + iw, y0 + ih, x0, y0 + ih, r);
        ctx.arcTo(x0, y0 + ih, x0, y0, r);
        ctx.arcTo(x0, y0, x0 + iw, y0, r);
        ctx.closePath();
      }
      ctx.fillStyle = cl.fill || '#ffffff';
      ctx.fill();
    }
    ctx.restore();
  }
  // Name text shrinks to the white area so it never runs into the outline.
  function classicTextFactor(shapeKey, w, h, bleedPx, pxPerMm, cl) {
    const t = classicOutlinePx(shapeKey, pxPerMm, cl);
    const lw = w - 2 * bleedPx, lh = h - 2 * bleedPx;
    return Math.max(0.6, Math.min((lw - 2 * t) / lw, (lh - 2 * t) / lh));
  }
  function hexRgb(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!m) return null;
    const n = parseInt(m[1], 16);
    return [n >> 16, (n >> 8) & 255, n & 255];
  }

  // Exact Euclidean distance transform (Felzenszwalb–Huttenlocher)
  function edt1d(f, n, d) {
    const INF = 1e12;
    const v = new Int32Array(n);
    const z = new Float64Array(n + 1);
    let k = 0;
    v[0] = 0; z[0] = -INF; z[1] = INF;
    for (let q = 1; q < n; q++) {
      let s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
      while (s <= z[k]) {
        k--;
        s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
      }
      k++; v[k] = q; z[k] = s; z[k + 1] = INF;
    }
    k = 0;
    for (let q = 0; q < n; q++) {
      while (z[k + 1] < q) k++;
      d[q] = (q - v[k]) * (q - v[k]) + f[v[k]];
    }
  }

  function edt(on, w, h) {
    const INF = 1e12;
    const g = new Float64Array(w * h);
    for (let i = 0; i < w * h; i++) g[i] = on[i] ? 0 : INF;
    const maxDim = Math.max(w, h);
    const f = new Float64Array(maxDim);
    const d = new Float64Array(maxDim);
    for (let x = 0; x < w; x++) {
      for (let y = 0; y < h; y++) f[y] = g[y * w + x];
      edt1d(f, h, d);
      for (let y = 0; y < h; y++) g[y * w + x] = d[y];
    }
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) f[x] = g[y * w + x];
      edt1d(f, w, d);
      for (let x = 0; x < w; x++) g[y * w + x] = d[x];
    }
    return g;
  }

  // White "sticker plate": round-kernel dilate + tiny closing, soft 1px edge
  function makePlate(w, h, drawFn, dilatePx, erodePx) {
    const A = document.createElement('canvas');
    A.width = w; A.height = h;
    drawFn(A.getContext('2d'));
    const src = A.getContext('2d').getImageData(0, 0, w, h).data;
    const n = w * h;
    const on = new Uint8Array(n);
    for (let i = 0; i < n; i++) on[i] = src[i * 4 + 3] > 127 ? 1 : 0;
    const dGlyph = edt(on, w, h);
    const dr2 = dilatePx * dilatePx;
    const outside = new Uint8Array(n);
    for (let i = 0; i < n; i++) outside[i] = dGlyph[i] <= dr2 ? 0 : 1;
    const dOut = edt(outside, w, h);
    const out = new ImageData(w, h);
    const px = out.data;
    for (let i = 0; i < n; i++) {
      let a = Math.sqrt(dOut[i]) - erodePx;
      a = a < 0 ? 0 : a > 1 ? 1 : a;
      const o = i * 4;
      px[o] = 255; px[o + 1] = 255; px[o + 2] = 255; px[o + 3] = (a * 255) | 0;
    }
    const P = document.createElement('canvas');
    P.width = w; P.height = h;
    P.getContext('2d').putImageData(out, 0, 0);
    return P;
  }

  function rotateCanvas(c) {
    const r = document.createElement('canvas');
    r.width = c.height; r.height = c.width;
    const ctx = r.getContext('2d');
    ctx.translate(r.width / 2, r.height / 2);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(c, -c.width / 2, -c.height / 2);
    return r;
  }

  function mirrorCanvas(c) {
    const r = document.createElement('canvas');
    r.width = c.width; r.height = c.height;
    const ctx = r.getContext('2d');
    ctx.translate(c.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(c, 0, 0);
    return r;
  }

  function dominantColour(img) {
    try {
      const S = 32;
      const c = document.createElement('canvas');
      c.width = S; c.height = S;
      const ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0, S, S);
      const d = ctx.getImageData(0, 0, S, S).data;
      const buckets = {};
      for (let i = 0; i < d.length; i += 4) {
        const k = ((d[i] >> 4) << 8) | ((d[i + 1] >> 4) << 4) | (d[i + 2] >> 4);
        const b = buckets[k] = buckets[k] || [0, 0, 0, 0];
        b[0] += d[i]; b[1] += d[i + 1]; b[2] += d[i + 2]; b[3]++;
      }
      let best = null;
      for (const k in buckets) if (!best || buckets[k][3] > best[3]) best = buckets[k];
      return best ? [Math.round(best[0] / best[3]), Math.round(best[1] / best[3]), Math.round(best[2] / best[3])] : null;
    } catch (e) { return null; }
  }

  const _imgCache = {};
  function loadImage(url) {
    if (!url) return Promise.resolve(null);
    if (_imgCache[url]) return _imgCache[url];
    _imgCache[url] = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    });
    return _imgCache[url];
  }

  // Render ONE label. opts: { first, last, colourHex, font {family,label,y,scale,lineSpace},
  //   img, dpi, bleedMm, opaque, bagtagCfg, bagtagFont }
  async function renderLabelCanvas(shapeKey, opts) {
    const dpi = opts.dpi || 300;
    const pxPerMm = dpi / 25.4;
    const s = LBL_SHAPES[shapeKey] || LBL_SHAPES.rect;
    const bleedPx = Math.round((opts.bleedMm || 0) * pxPerMm);
    const w = Math.round(s.wMm * pxPerMm) + bleedPx * 2;
    const h = Math.round(s.hMm * pxPerMm) + bleedPx * 2;
    const previewLabelW = s.previewW;
    const printScale = Math.round(s.wMm * pxPerMm) / previewLabelW;

    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    const isPrint = opts.opaque !== false;
    ctx.fillStyle = '#ffffff';
    if (isPrint) {
      ctx.fillRect(0, 0, w, h);
    } else {
      ctx.save();
      shapePath(ctx, w, h, shapeKey);
      ctx.clip('evenodd');
      ctx.fillRect(0, 0, w, h);
    }

    const classic = opts.classic !== undefined ? opts.classic : CLASSIC;
    const drawClassic = !opts.img && !!classic;
    if (drawClassic) drawClassicLabel(ctx, w, h, shapeKey, bleedPx, pxPerMm, classic, 9.105 / LBL_BAGTAG_TPL.cutW);
    if (opts.img) {
      let zoom = shapeKey === 'circle' ? 1.05 : 1;
      // Background zoom only applies to name-label pattern art (image 2). A
      // bag-tag library/matched background is already composed to the tag, so
      // it fits the art box exactly at 100% — never zoomed/cropped.
      if (shapeKey === 'bagtag' && !opts.bagtagFullArtwork && opts.bagtagCfg && opts.bagtagCfg.bgZoomPct) {
        zoom = Math.max(0.5, opts.bagtagCfg.bgZoomPct / 100);
      }
      const scale = Math.max(w / opts.img.width, h / opts.img.height) * zoom;
      const dw = opts.img.width * scale, dh = opts.img.height * scale;
      ctx.drawImage(opts.img, (w - dw) / 2, (h - dh) / 2, dw, dh);
    }

    const first = opts.first, last = opts.last;
    const font = opts.font;
    const tall = (font.y || 0) !== 0;
    let lines, fsPreview, strokes;
    if (shapeKey === 'pencil' || shapeKey === 'mini') {
      const combined = [first, last].filter(Boolean).join(' ');
      lines = [combined];
      fsPreview = fontPx(shapeKey, fitScale(combined, s.threshold), previewLabelW, false, tall);
      strokes = [s.stroke];
    } else if (shapeKey === 'bagtag') {
      lines = [first];
      fsPreview = fontPx('bagtag', fitScale(first, s.threshold), previewLabelW, false, tall);
      strokes = [s.stroke];
    } else {
      lines = last ? [first, last] : [first];
      const longer = last && last.length > first.length ? last : first;
      fsPreview = fontPx(shapeKey, fitScale(longer, s.threshold), previewLabelW, !!last, tall);
      strokes = last ? [s.stroke, shapeKey === 'circle' ? 11 : s.stroke] : [s.stroke];
    }
    let fs = fsPreview * printScale * (font.scale || 1);
    if (drawClassic) fs *= classicTextFactor(shapeKey, w, h, bleedPx, pxPerMm, classic);
    const btCfg = (shapeKey === 'bagtag' && opts.bagtagCfg) ? opts.bagtagCfg : null;
    const btFont = (shapeKey === 'bagtag' && opts.bagtagFont) ? opts.bagtagFont : null;
    if (btCfg && btCfg.nameScalePct) fs *= btCfg.nameScalePct / 100;
    if (btFont && btFont.scalePct) fs *= btFont.scalePct / 100;
    try { await document.fonts.load(Math.ceil(fs) + 'px "' + font.family + '"'); } catch (e) {}

    const lineH = fs * 1.05 * (font.lineSpace || 1);
    const gapAdjust = ((shapeKey === 'rect' || shapeKey === 'bagtag') && lines.length === 2) ? -6 * printScale : 0;
    const blockH = lines.length * lineH + gapAdjust;
    const vOff = (font.y || 0) * printScale;
    const centreY = btCfg
      ? h * (1 - (((btCfg.namePosPct !== undefined ? btCfg.namePosPct : 18) + (btFont ? (btFont.posOffsetPct || 0) : 0)) / 100))
      : h / 2;
    const startY = centreY - blockH / 2 + lineH / 2 + vOff;

    const drawAllLines = (c, fillStyle) => {
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      c.fillStyle = fillStyle;
      let y = startY;
      lines.forEach((line, i) => {
        c.font = fs + 'px "' + font.family + '"';
        c.fillText(line, w / 2, y);
        y += lineH + (i === 0 ? gapAdjust : 0);
      });
    };

    // White "sticker plate" surround behind the text so it reads on any
    // background — drawn on all shapes including bag tags (restored 2026-09-10
    // at Damian's request).
    {
      const close = Math.max(1, 0.02 * fs);
      const maxStroke = Math.max.apply(null, strokes) * printScale;
      const plate = makePlate(w, h, (c) => drawAllLines(c, '#000'), maxStroke / 2 + close, close);
      ctx.drawImage(plate, 0, 0);
    }
    drawAllLines(ctx, opts.colourHex);

    if (!isPrint) ctx.restore();
    return canvas;
  }

  /* ── PDF libraries (lazy, CDN) ─────────────────────────────────────────── */
  let _jsPdfP = null;
  function loadJsPdf() {
    if (window.jspdf && window.jspdf.jsPDF) return Promise.resolve(window.jspdf.jsPDF);
    if (_jsPdfP) return _jsPdfP;
    _jsPdfP = new Promise((resolve, reject) => {
      const sc = document.createElement('script');
      sc.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      sc.onload = () => window.jspdf && window.jspdf.jsPDF ? resolve(window.jspdf.jsPDF) : reject(new Error('jsPDF failed'));
      sc.onerror = () => reject(new Error('Could not load jsPDF'));
      document.head.appendChild(sc);
    });
    return _jsPdfP;
  }
  let _pdfLibP = null;
  function loadPdfLib() {
    if (window.PDFLib && window.PDFLib.PDFDocument) return Promise.resolve(window.PDFLib);
    if (_pdfLibP) return _pdfLibP;
    _pdfLibP = new Promise((resolve, reject) => {
      const sc = document.createElement('script');
      sc.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf-lib/1.17.1/pdf-lib.min.js';
      sc.onload = () => window.PDFLib && window.PDFLib.PDFDocument ? resolve(window.PDFLib) : reject(new Error('pdf-lib failed'));
      sc.onerror = () => reject(new Error('Could not load pdf-lib'));
      document.head.appendChild(sc);
    });
    return _pdfLibP;
  }

  /* ── Spot-colour post-processor (identical to admin) ──────────────────── */
  function hexToRgb(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    const n = m ? parseInt(m[1], 16) : 0xFF00FF;
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  function applySpotColour(pdf, spotName, rgb) {
    const OBJ_RE = () => /(?:^|\n)(\d+) 0 obj\s*(?=<<|\[|stream)/g;
    let maxObj = 0;
    const objRe = OBJ_RE();
    let m;
    while ((m = objRe.exec(pdf))) maxObj = Math.max(maxObj, parseInt(m[1]));
    const fnObj = maxObj + 1, csObj = maxObj + 2;
    const name = String(spotName || 'CutContour').replace(/[^A-Za-z0-9._-]/g, '');
    const rgbn = rgb.map((v) => (v / 255).toFixed(4));
    const newObjs =
      fnObj + ' 0 obj\n<< /FunctionType 2 /Domain [0 1] /C0 [1 1 1] /C1 [' + rgbn[0] + ' ' + rgbn[1] + ' ' + rgbn[2] + '] /N 1 >>\nendobj\n' +
      csObj + ' 0 obj\n[/Separation /' + name + ' /DeviceRGB ' + fnObj + ' 0 R]\nendobj\n';
    const xrefPos = pdf.lastIndexOf('\nxref\n') + 1;
    pdf = pdf.slice(0, xrefPos) + newObjs + pdf.slice(xrefPos);
    pdf = pdf.replace(/<<\s*\n?\/ProcSet/g, '<<\n/ColorSpace <</ArloCutCS ' + csObj + ' 0 R>>\n/ProcSet');
    const offsets = {};
    const objRe2 = OBJ_RE();
    let m2;
    while ((m2 = objRe2.exec(pdf))) {
      offsets[parseInt(m2[1])] = m2.index + (pdf[m2.index] === '\n' ? 1 : 0);
    }
    const size = csObj + 1;
    const trailerSrc = pdf.slice(pdf.lastIndexOf('trailer'));
    const rootRef = (trailerSrc.match(/\/Root (\d+ 0 R)/) || [])[1];
    const infoRef = (trailerSrc.match(/\/Info (\d+ 0 R)/) || [])[1];
    const idRef = (trailerSrc.match(/\/ID (\[[^\]]*\])/) || [])[1];
    const newXrefPos = pdf.lastIndexOf('\nxref\n') + 1;
    const out = pdf.slice(0, newXrefPos);
    const xrefStart = out.length;
    let xref = 'xref\n0 ' + size + '\n0000000000 65535 f \n';
    for (let i = 1; i < size; i++) {
      const off = offsets[i];
      xref += (off === undefined ? '0000000000 65535 f \n' : String(off).padStart(10, '0') + ' 00000 n \n');
    }
    let trailer = 'trailer\n<<\n/Size ' + size;
    if (rootRef) trailer += '\n/Root ' + rootRef;
    if (infoRef) trailer += '\n/Info ' + infoRef;
    if (idRef) trailer += '\n/ID ' + idRef;
    trailer += '\n>>\nstartxref\n' + xrefStart + '\n%%EOF';
    return out + xref + trailer;
  }

  function drawCutPath(doc, shapeKey, x, y, wMm, hMm) {
    if (shapeKey === 'circle') {
      doc.circle(x + wMm / 2, y + hMm / 2, Math.min(wMm, hMm) / 2, 'S');
    } else {
      const r = (shapeKey === 'pencil' || shapeKey === 'mini') ? Math.min(wMm, hMm) / 2 : (22 / 336) * Math.max(wMm, hMm);
      doc.roundedRect(x, y, wMm, hMm, r, r, 'S');
    }
  }

  /* ── Sheet composer (identical logic to admin; one blob per page) ─────── */
  // contents: rows {type, qty}; opts adds: imgFor(type)→Image, mirror
  async function composeSheetPdfs(contents, opts, onProgress) {
    const jsPDF = await loadJsPdf();
    const sheet = cfg.sheet;
    const pageW = sheet.pageWMm, pageH = sheet.pageHMm;
    const margin = sheet.marginMm, gap = sheet.gapMm;
    const usableW = pageW - margin * 2;
    const bottomLimit = pageH - margin;
    const orient = pageW > pageH ? 'landscape' : 'portrait';
    const drawCut = sheet.cutLines !== false;
    const cutBelow = sheet.cutLayer !== 'above';
    const bleed = Math.max(0, sheet.bleedMm || 0);
    const effGap = Math.max(gap, bleed);
    const usableH = bottomLimit - margin;
    const groups = (contents || []).filter((c) => c.qty > 0 && LBL_SHAPES[c.type] && c.type !== 'bagtag');

    // Pass 1: render + plan
    const plans = [];
    for (let gi = 0; gi < groups.length; gi++) {
      const g = groups[gi];
      const shape = LBL_SHAPES[g.type];
      const wMm = shape.wMm, hMm = shape.hMm;
      if (onProgress) onProgress('Preparing ' + shape.label.toLowerCase() + ' labels…');
      let canvas = await renderLabelCanvas(g.type, Object.assign({}, opts, { bleedMm: bleed, img: opts.imgFor(g.type) }));
      if (opts.mirror) canvas = mirrorCanvas(canvas);
      let rotated = false;
      if (sheet.autoRotate !== false && wMm !== hMm) {
        const fit = (lw, lh) =>
          Math.max(1, Math.floor((usableW + effGap) / (lw + effGap))) *
          Math.max(1, Math.floor((usableH + effGap) / (lh + effGap)));
        rotated = fit(hMm, wMm) > fit(wMm, hMm);
      }
      const gw = rotated ? hMm : wMm;
      const gh = rotated ? wMm : hMm;
      if (rotated) canvas = rotateCanvas(canvas);
      const perRow = Math.max(1, Math.floor((usableW + effGap) / (gw + effGap)));
      const gridCols = Math.min(perRow, g.qty);
      const gridW = gridCols * gw + (gridCols - 1) * effGap;
      plans.push({
        type: g.type, qty: g.qty, gw, gh, perRow,
        startX: Math.max(margin, (pageW - gridW) / 2),
        dataUrl: canvas.toDataURL('image/jpeg', 0.92),
        alias: 'lblgrp' + gi + (rotated ? 'r' : ''),
      });
    }

    // Pass 2: paginate (no drawing) for vertical centring
    const ops = [];
    let pageIdx = 0;
    let cursorY = margin;
    const pageBottom = [margin];
    plans.forEach((plan) => {
      let count = 0;
      while (count < plan.qty) {
        if (cursorY + plan.gh > bottomLimit) { pageIdx++; cursorY = margin; pageBottom[pageIdx] = margin; }
        const rowCount = Math.min(plan.perRow, plan.qty - count);
        ops.push({ page: pageIdx, plan, y: cursorY, rowCount });
        pageBottom[pageIdx] = cursorY + plan.gh;
        cursorY += plan.gh + effGap;
        count += rowCount;
      }
      cursorY += 3;
    });
    const yOffset = pageBottom.map((b) => Math.max(0, (bottomLimit - b) / 2));

    // Pass 3: one single-page doc per page
    // Classic designs: always the outline colour (it's what makes the bleed work).
    const pageBgRgb = (!opts.imgFor('rect') && CLASSIC) ? hexRgb(CLASSIC.outline)
      : (sheet.pageBg !== false && opts.imgFor('rect')) ? dominantColour(opts.imgFor('rect')) : null;
    const blobs = [];
    for (let p = 0; p < pageBottom.length; p++) {
      const doc = new jsPDF({ unit: 'mm', format: [pageW, pageH], orientation: orient, compress: false });
      const emitCut = (type, x, y, wMm, hMm) => {
        doc.internal.write('/ArloCutCS CS 1 SCN');
        doc.setLineWidth(0.2);
        drawCutPath(doc, type, x, y, wMm, hMm);
      };
      if (pageBgRgb) {
        doc.setFillColor(pageBgRgb[0], pageBgRgb[1], pageBgRgb[2]);
        doc.rect(0, 0, pageW, pageH, 'F');
      }
      ops.filter((op) => op.page === p).forEach((op) => {
        const plan = op.plan;
        const y = op.y + yOffset[p];
        const cellW = plan.gw + bleed * 2, cellH = plan.gh + bleed * 2;
        let x = plan.startX;
        for (let i = 0; i < op.rowCount; i++) {
          if (drawCut && cutBelow) emitCut(plan.type, x, y, plan.gw, plan.gh);
          doc.addImage(plan.dataUrl, 'JPEG', x - bleed, y - bleed, cellW, cellH, plan.alias);
          if (drawCut && !cutBelow) emitCut(plan.type, x, y, plan.gw, plan.gh);
          x += plan.gw + effGap;
        }
      });
      if (drawCut) {
        const s = doc.output(); // raw binary string — do NOT TextDecoder('latin1') (win-1252 corrupts JPEGs)
        const fixed = applySpotColour(s, sheet.cutSpotName || 'CutContour', hexToRgb(sheet.cutColour));
        const bytes = new Uint8Array(fixed.length);
        for (let i = 0; i < fixed.length; i++) bytes[i] = fixed.charCodeAt(i) & 0xff;
        blobs.push(new Blob([bytes], { type: 'application/pdf' }));
      } else {
        blobs.push(new Blob([doc.output('arraybuffer')], { type: 'application/pdf' }));
      }
    }
    return blobs;
  }

  /* ── Bag tag composer: laser template used exactly (identical to admin) ── */
  async function composeBagTagPdf(opts, onProgress) {
    const PDFLib = await loadPdfLib();
    if (onProgress) onProgress('Preparing bag tag…');
    const tplBytes = await fetch(ASSETS.bagtagTpl).then((r) => {
      if (!r.ok) throw new Error('Bag tag template not found');
      return r.arrayBuffer();
    });
    const doc = await PDFLib.PDFDocument.load(tplBytes);
    const page = doc.getPage(0);
    const M = (LBL_BAGTAG_TPL.pageMarginMm || 5) * 72 / 25.4;
    const cb = LBL_BAGTAG_TPL.content;
    page.setMediaBox(cb.x0 - M, cb.y0 - M, (cb.x1 - cb.x0) + M * 2, (cb.y1 - cb.y0) + M * 2);
    page.setCropBox(cb.x0 - M, cb.y0 - M, (cb.x1 - cb.x0) + M * 2, (cb.y1 - cb.y0) + M * 2);

    // No bleed: artwork stays in the template's fill box, inset ~0.35mm from
    // the cut line (the laser cuts unprinted stock — same as the hole offset)
    const canvas = await renderLabelCanvas('bagtag', Object.assign({}, opts, { bleedMm: 0, img: opts.imgFor('bagtag') }));
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const jpgBytes = Uint8Array.from(atob(dataUrl.split(',')[1]), (ch) => ch.charCodeAt(0));
    const jpg = await doc.embedJpg(jpgBytes);

    const art = LBL_BAGTAG_TPL.art;
    const X = art.x, Y = art.y, W = art.w, H = art.h;
    const R = W / 2, rb = art.cornerR, k = 0.5523;
    const hole = LBL_BAGTAG_TPL.hole;
    page.pushOperators(
      PDFLib.pushGraphicsState(),
      PDFLib.moveTo(X + rb, Y),
      PDFLib.lineTo(X + W - rb, Y),
      PDFLib.appendBezierCurve(X + W - rb + k * rb, Y, X + W, Y + rb - k * rb, X + W, Y + rb),
      PDFLib.lineTo(X + W, Y + H - R),
      PDFLib.appendBezierCurve(X + W, Y + H - R + k * R, X + R + k * R, Y + H, X + R, Y + H),
      PDFLib.appendBezierCurve(X + R - k * R, Y + H, X, Y + H - R + k * R, X, Y + H - R),
      PDFLib.lineTo(X, Y + rb),
      PDFLib.appendBezierCurve(X, Y + rb - k * rb, X + rb - k * rb, Y, X + rb, Y),
      PDFLib.closePath(),
      PDFLib.moveTo(hole[0][0], hole[0][1]),
      PDFLib.appendBezierCurve.apply(null, hole[1]),
      PDFLib.appendBezierCurve.apply(null, hole[2]),
      PDFLib.appendBezierCurve.apply(null, hole[3]),
      PDFLib.appendBezierCurve.apply(null, hole[4]),
      PDFLib.closePath(),
      PDFLib.clipEvenOdd(),
      PDFLib.endPath()
    );
    page.drawImage(jpg, { x: X, y: Y, width: W, height: H });
    page.pushOperators(PDFLib.popGraphicsState());
    const bytes = await doc.save({ useObjectStreams: false });
    return new Blob([bytes], { type: 'application/pdf' });
  }

  /* ── Uploadcare (same pipeline as the canvas PNGs) ─────────────────────── */
  function uploadToUploadcare(blob, filename) {
    const fd = new FormData();
    fd.append('UPLOADCARE_PUB_KEY', cfg.uploadcarePublicKey);
    fd.append('UPLOADCARE_STORE', '1');
    fd.append('file', blob, filename || 'labels.pdf');
    return fetch('https://upload.uploadcare.com/base/', { method: 'POST', body: fd })
      .then((res) => res.json())
      .then((d) => {
        if (!d.file) throw new Error('Upload failed');
        return 'https://ucarecdn.com/' + d.file + '/' + (filename || 'labels.pdf');
      });
  }

  /* ══════════════════════════ UI ══════════════════════════ */
  const state = { colourIdx: 0, fontIdx: 0, packIdx: null, shape: 'rect', upsells: {}, bgIdx: 0 };
  const el = (id) => document.getElementById(id);

  function currentFont() {
    const f = (cfg.fonts || [])[state.fontIdx] || { family: 'sans-serif', label: '?', yOffsetPx: 0, scalePct: 100, lineSpacePct: 100 };
    return {
      family: f.family,
      label: f.label,
      y: f.yOffsetPx || 0,
      scale: (f.scalePct || 100) / 100,
      lineSpace: (f.lineSpacePct || 100) / 100,
    };
  }
  function currentColour() {
    return (cfg.colours || [])[state.colourIdx] || { name: '', hex: '#00A896' };
  }

  // ── Bag Tag backgrounds ───────────────────────────────────────────────────
  // Merchant-managed library of bag-tag artworks (admin Labels → Bag Tag). Each
  // entry has { url, name, textColor:'white'|'black', productHandles:[...] }.
  // Two ways a background is chosen:
  //   • Standalone Acrylic Bag Tag product → the customer picks one (bgIdx).
  //   • Bag tag as a matching add-on on a name-label product → auto-selected by
  //     matching the current product's handle against a background's
  //     productHandles, so e.g. "skateboards" name labels get the skateboard
  //     bag-tag artwork with its own custom layout.
  // Either way the name text is drawn in the white/black the merchant set for
  // that background, not the font-colour palette (which stays for name labels).
  function bagBackgrounds() {
    return (cfg.bagtag && Array.isArray(cfg.bagtag.backgrounds)) ? cfg.bagtag.backgrounds : [];
  }
  function bgHandles(b) {
    if (!b) return [];
    var hs = Array.isArray(b.productHandles) ? b.productHandles : (b.productHandle ? [b.productHandle] : []);
    return hs.map(function (x) { return String(x || '').trim().toLowerCase(); }).filter(Boolean);
  }
  // Standalone Acrylic Bag Tag product with at least one background = picker mode.
  function bagtagUsesLibrary() {
    return !!(STANDALONE && STANDALONE.kind === 'bagtag' && bagBackgrounds().length);
  }
  function currentBagBg() {
    return bagBackgrounds()[state.bgIdx] || null;
  }
  // Background whose productHandles include this product — used for the matching
  // add-on / bag-tag preview shown on a name-label product page.
  function matchedBagBg() {
    var h = String(data.handle || '').toLowerCase();
    if (!h) return null;
    return bagBackgrounds().find(function (b) { return bgHandles(b).indexOf(h) !== -1; }) || null;
  }
  // The background that applies right now: picker selection when standalone,
  // otherwise the product-matched one (may be null on unmatched products).
  function effectiveBagBg() {
    return bagtagUsesLibrary() ? currentBagBg() : matchedBagBg();
  }
  function bgTextHex(b) {
    var mode = b && String(b.textColor).toLowerCase();
    // 'label' = match name-label style: use the customer's chosen font colour
    // (and the white surround is restored — see bagtagUsesLabelStyle).
    if (mode === 'label') return currentColour().hex;
    return mode === 'white' ? '#ffffff' : '#000000';
  }
  // True when the applicable bag-tag background is set to "Match name labels",
  // so the tag text takes the font colour + the white sticker-plate surround.
  function bagtagUsesLabelStyle() {
    var b = effectiveBagBg();
    return !!(b && String(b.textColor).toLowerCase() === 'label');
  }
  // Effective name-text colour for a given shape (bag-tag background overrides the palette).
  function textHexFor(shapeKey) {
    if (shapeKey === 'bagtag') {
      var b = effectiveBagBg();
      if (b) return bgTextHex(b);
    }
    return currentColour().hex;
  }

  function imgUrlFor(shapeKey) {
    // Bag-tag background (picked or product-matched) is the tag design artwork.
    if (shapeKey === 'bagtag') {
      var b = effectiveBagBg();
      if (b && b.url) return b.url;
    }
    // IMAGE 2 is the label background artwork for every shape. Image 1 is the
    // hero photo (Shopify uses it on collection pages, search and checkout),
    // and images 3+ are further photos — none are used as label artwork.
    if (CLASSIC) return '';
    return data.bgImage || '';
  }
  function bagtagFontCfg() {
    return (cfg.bagtag && cfg.bagtag.fonts) ? cfg.bagtag.fonts[state.fontIdx] : null;
  }

  /* ── Live preview (canvas, shared renderer, 2× resolution) ────────────── */
  let _pvSeq = 0;
  let _pvTimer = null;

  /* ── Preview scenes ───────────────────────────────────────────────────────
     Optional lifestyle photo behind a shape, with a transparent PNG in front,
     so the label reads as stuck to a real object. Preview only — the PDF
     pipeline never sees these. Placement percentages are exactly the ones set
     on the admin Labels page. */
  // Asset folder from a URL Liquid gave us, minus its cache-busting query —
  // each scene image appends its own version so replacing one actually shows.
  const SCENE_BASE = (root.dataset.fontVic || '').split('?')[0]
    .replace(/arlo-font-vic-modern-cursive\.ttf$/, '');
  const _sceneImgs = {};
  function sceneAssetUrl(name, version) {
    return name && SCENE_BASE ? SCENE_BASE + name + (version ? '?v=' + version : '') : '';
  }
  function sceneFor(shapeKey) {
    const sc = (cfg.scenes || {})[shapeKey];
    return sc && sc.enabled && sc.bgAsset ? sc : null;
  }
  // Returns the image if it's ready, otherwise kicks off a load and redraws.
  function sceneImage(name, version) {
    const url = sceneAssetUrl(name, version);
    if (!url) return null;
    if (_sceneImgs[url] !== undefined) return _sceneImgs[url];
    _sceneImgs[url] = null;
    const img = new Image();
    img.onload = () => { _sceneImgs[url] = img; schedulePreview(); };
    img.onerror = () => { _sceneImgs[url] = null; };
    img.src = url;
    return null;
  }

  function drawSceneOnto(cv, ctx, sc, labelCanvas) {
    const bg = sceneImage(sc.bgAsset, sc.bgAssetV);
    if (!bg) return false;                       // not ready — keep the plain label
    const W = cv.width;
    const H = Math.round(W * bg.height / bg.width);
    if (cv.height !== H) cv.height = H;
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(bg, 0, 0, W, H);

    const p = sc.placement || { centreXPct: 50, centreYPct: 50, widthPct: 40, rotationDeg: 0 };
    const lw = W * (p.widthPct / 100);
    const lh = lw * (labelCanvas.height / labelCanvas.width);
    ctx.save();
    ctx.translate(W * (p.centreXPct / 100), H * (p.centreYPct / 100));
    if (p.rotationDeg) ctx.rotate(p.rotationDeg * Math.PI / 180);
    ctx.drawImage(labelCanvas, -lw / 2, -lh / 2, lw, lh);
    ctx.restore();

    const fg = sceneImage(sc.fgAsset, sc.fgAssetV);
    if (fg) ctx.drawImage(fg, 0, 0, W, H);
    return true;
  }

  function fitCanvasToStage(cv, shapeKey) {
    const stage = el('arlo-lp-stage');
    if (!stage || !cv) return;
    const box = stage.getBoundingClientRect();
    const availW = box.width, availH = box.height;
    if (!availW || !availH) return;
    const s = LBL_SHAPES[shapeKey] || LBL_SHAPES.rect;
    // In scene mode the canvas is the photo's shape, not the label's
    const sc = sceneFor(shapeKey);
    const sceneBg = sc ? sceneImage(sc.bgAsset, sc.bgAssetV) : null;
    const ratio = sceneBg ? (sceneBg.width / sceneBg.height) : (s.wMm / s.hMm);
    let w = availW, h = w / ratio;
    if (h > availH) { h = availH; w = h * ratio; }
    cv.style.width = Math.floor(w) + 'px';
    cv.style.height = Math.floor(h) + 'px';
  }

  function fitAllCanvases() {
    PREVIEW_SHAPES.forEach((k) => {
      fitCanvasToStage(root.querySelector('[data-lp-canvas="' + k + '"]'), k);
    });
  }
  async function redrawPreview() {
    const seq = ++_pvSeq;
    const first = el('arlo-lp-first').value.trim() || 'Your Name';
    const last = el('arlo-lp-last').value.trim();
    const colour = currentColour();
    const font = currentFont();
    // upsell card thumbnails
    for (const cv of root.querySelectorAll('[data-lp-upsell-shape]')) {
      const shapeKey = cv.dataset.lpUpsellShape;
      // Shape-aware so the bag-tag add-on thumbnail uses the matched background
      // artwork + its white/black text, matching the main Bag Tag preview.
      const img = await loadImage(imgUrlFor(shapeKey));
      if (seq !== _pvSeq) return;
      const s2 = LBL_SHAPES[shapeKey];
      const c = await renderLabelCanvas(shapeKey, {
        first, last, colourHex: textHexFor(shapeKey), font, img,
        dpi: (s2.previewW * 2 / s2.wMm) * 25.4,
        opaque: false, bagtagCfg: cfg.bagtag, bagtagFont: bagtagFontCfg(),
        bagtagPlate: shapeKey === 'bagtag' && bagtagUsesLabelStyle(),
        bagtagFullArtwork: shapeKey === 'bagtag' && !!effectiveBagBg(),
      });
      if (seq !== _pvSeq) return;
      const mctx = cv.getContext('2d');
      mctx.clearRect(0, 0, cv.width, cv.height);
      mctx.drawImage(c, 0, 0, cv.width, cv.height);
    }
    for (const shapeKey of PREVIEW_SHAPES) {
      const cv = root.querySelector('[data-lp-canvas="' + shapeKey + '"]');
      if (!cv) continue;
      const s = LBL_SHAPES[shapeKey];
      const img = await loadImage(imgUrlFor(shapeKey));
      if (seq !== _pvSeq) return;
      const dpiPv = (s.previewW * 2 / s.wMm) * 25.4;
      const c = await renderLabelCanvas(shapeKey, {
        first, last, colourHex: textHexFor(shapeKey), font, img, dpi: dpiPv, opaque: false,
        bagtagCfg: cfg.bagtag,
        bagtagFont: bagtagFontCfg(),
        bagtagPlate: shapeKey === 'bagtag' && bagtagUsesLabelStyle(),
        bagtagFullArtwork: shapeKey === 'bagtag' && !!effectiveBagBg(),
      });
      if (seq !== _pvSeq) return;
      const ctx = cv.getContext('2d');
      ctx.clearRect(0, 0, cv.width, cv.height);
      const sc = sceneFor(shapeKey);
      if (!sc || !drawSceneOnto(cv, ctx, sc, c)) {
        ctx.drawImage(c, 0, 0, cv.width, cv.height);
      }
      fitCanvasToStage(cv, shapeKey);
    }
  }
  function schedulePreview() {
    clearTimeout(_pvTimer);
    _pvTimer = setTimeout(redrawPreview, 60);
  }

  function setShape(shape) {
    state.shape = shape;
    root.querySelectorAll('[data-lp-shape]').forEach((b) => b.classList.toggle('active', b.dataset.lpShape === shape));
    root.querySelectorAll('[data-lp-label]').forEach((d) => d.classList.toggle('active', d.dataset.lpLabel === shape));
    const s = LBL_SHAPES[shape];
    el('arlo-lp-size').textContent = s.size + ' · Waterproof · Dishwasher-safe';
    fitCanvasToStage(root.querySelector('[data-lp-canvas="' + shape + '"]'), shape);
    snapToPreview(); // picking a shape always shows the previewer
  }

  /* ── Build controls from config ───────────────────────────────────────── */
  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function buildControls() {
    if (STANDALONE) {
      root.querySelectorAll('[data-lp-standalone-hide]').forEach((n) => { n.style.display = 'none'; });
    }
    // Colours — bag-tag standalone with a background library swaps the colour
    // swatches for background-artwork thumbnails; the name text then uses the
    // white/black colour the merchant set for the chosen background.
    if (bagtagUsesLibrary()) {
      el('arlo-lp-swatches').innerHTML = bagBackgrounds().map((b, i) =>
        '<button type="button" class="arlo-lp__swatch arlo-lp__bg-swatch' + (i === state.bgIdx ? ' active' : '')
        + '" title="' + (b.name || 'Background ' + (i + 1)) + '" aria-label="' + (b.name || 'Background ' + (i + 1))
        + '" style="background-image:url(' + (b.url || '') + ');background-size:cover;background-position:center" data-lp-bg="' + i + '"></button>'
      ).join('');
      const cur = currentBagBg();
      const label = el('arlo-lp-colour-name');
      if (label) {
        var mode = cur && String(cur.textColor).toLowerCase();
        var modeLabel = mode === 'label' ? 'Name-label style'
          : (bgTextHex(cur) === '#ffffff' ? 'White text' : 'Black text');
        label.textContent = (cur && cur.name ? cur.name : 'Background') + ' · ' + modeLabel;
      }
      const title = document.getElementById('arlo-lp-colour-title');
      if (title) title.textContent = 'Pick your background';
    } else {
      el('arlo-lp-swatches').innerHTML = (cfg.colours || []).map((c, i) =>
        '<button type="button" class="arlo-lp__swatch' + (i === state.colourIdx ? ' active' : '') + '" title="' + c.name + '" aria-label="' + c.name + '" style="background:' + c.hex + '" data-lp-colour="' + i + '"></button>'
      ).join('');
      el('arlo-lp-colour-name').textContent = currentColour().name;
    }

    // Fonts
    el('arlo-lp-fonts').innerHTML = (cfg.fonts || []).map((f, i) =>
      '<button type="button" class="arlo-lp__font-card' + (i === state.fontIdx ? ' active' : '') + '" data-lp-font="' + i + '">'
      + '<span class="arlo-lp__font-name">' + f.label + '</span>'
      + '<span class="arlo-lp__font-sample" style="font-family:\'' + f.family + '\'">Aa</span>'
      + '</button>'
    ).join('');

    if (!STANDALONE) buildUpsell();

    // Packs ↔ variants BY POSITION; only show packs with a live variant
    if (STANDALONE) {
      // Variant chooser (only when the product has real options to pick)
      const vs = data.variants.filter((v) => v.available !== false);
      el('arlo-lp-packs').innerHTML = vs.length > 1 ? vs.map((v, i) =>
        '<div class="arlo-lp__pack' + (state.packIdx === i ? ' active' : '') + '" data-lp-pack="' + i + '">'
        + '<div class="arlo-lp__pack-name">' + v.title + '</div>'
        + '<div class="arlo-lp__pack-price">' + money(v.price) + '</div>'
        + '</div>').join('') : '';
      if (vs.length === 1) state.packIdx = data.variants.indexOf(vs[0]);
      if (STANDALONE.blurb) {
        const cap = el('arlo-lp-standalone-blurb');
        if (cap) { cap.textContent = STANDALONE.blurb; cap.style.display = 'block'; }
      }
      return;
    }
    el('arlo-lp-packs').innerHTML = (cfg.packs || []).map((p, i) => {
      const v = data.variants[i];
      if (!v || v.available === false) return '';
      const totalQty = (p.contents || []).reduce((sum, c) => sum + (c.qty || 0), 0);
      const per = totalQty ? Math.round(v.price / totalQty) : 0;
      return '<div class="arlo-lp__pack' + (state.packIdx === i ? ' active' : '') + '" data-lp-pack="' + i + '">'
        + (p.badge ? '<span class="arlo-lp__pack-badge">' + p.badge + '</span>' : '')
        + '<div class="arlo-lp__pack-name">' + (p.name || v.title) + '</div>'
        + '<div class="arlo-lp__pack-desc">' + (p.description || '') + '</div>'
        + '<div class="arlo-lp__pack-price">' + money(v.price) + (per ? '<span class="arlo-lp__pack-per">' + per + '¢/ea</span>' : '') + '</div>'
        + '<div class="arlo-lp__pack-help" data-lp-help="' + i + '">What\'s included?</div>'
        + '<div class="arlo-lp__pack-contents">' + (p.contents || []).map((c) => {
            const sh = LBL_SHAPES[c.type];
            // admin can override how a row reads (e.g. pencil-size labels sold
            // as "Iron-On Clothing Labels" — the shape name confuses customers)
            const name = c.displayName || ((c.note ? c.note + ' ' : '') + (sh ? sh.label : c.type));
            return '• ' + c.qty + ' × ' + name;
          }).join('<br>') + '</div>'
        + '</div>';
    }).join('');
  }

  function activeUpsells() {
    return (cfg.upsells || []).filter((u) => u.enabled && u.variantId);
  }

  function buildUpsell() {
    const box = el('arlo-lp-upsell');
    if (!box) return;
    const list = activeUpsells();
    if (!list.length) { box.style.display = 'none'; return; }
    box.style.display = 'block';
    // Build the cards ONCE. Re-rendering the HTML on every control change
    // recreated the thumbnail canvases blank and made them flash before the
    // async repaint landed — after the first build we only sync the ticked
    // state, leaving the painted canvases untouched.
    if (box.dataset.built === '1') {
      list.forEach((u) => {
        const card = box.querySelector('[data-lp-upsell="' + u.id + '"]');
        if (card) card.classList.toggle('active', !!state.upsells[u.id]);
      });
      return;
    }
    // Badge always states the dollar saving (never a %), so the two add-ons
    // read consistently: "SAVE $8" / "SAVE $6.25". Falls back to whatever the
    // admin typed only when there is no price difference to quote.
    const savingBadge = (u) => {
      const full = Number(u.fullPrice || 0), disc = Number(u.discountedPrice || 0);
      if (!(full > disc && disc > 0)) return u.badge || '';
      const diff = full - disc;
      return 'SAVE $' + (Math.abs(diff - Math.round(diff)) < 0.005 ? String(Math.round(diff)) : diff.toFixed(2));
    };
    box.innerHTML = list.map((u) => {
      const on = !!state.upsells[u.id];
      const badge = savingBadge(u);
      const was = u.fullPrice && u.discountedPrice && u.fullPrice > u.discountedPrice
        ? '<span class="arlo-lp__upsell-was">$' + Number(u.fullPrice).toFixed(2) + '</span>' : '';
      // thumbnail: bag tags show the tag, sheet add-ons show their first label type
      const thumbShape = u.kind === 'bagtag' ? 'bagtag'
        : ((u.contents && u.contents[0] && LBL_SHAPES[u.contents[0].type]) ? u.contents[0].type : 'rect');
      const sh = LBL_SHAPES[thumbShape];
      const cw = 240, ch = Math.round(cw / (sh.wMm / sh.hMm));
      return '<div class="arlo-lp__upsell-card' + (on ? ' active' : '') + '" data-lp-upsell="' + u.id + '">'
        + (badge ? '<span class="arlo-lp__upsell-badge">' + badge + '</span>' : '')
        + '<span class="arlo-lp__upsell-check">✓</span>'
        + '<span class="arlo-lp__upsell-thumb"><canvas data-lp-canvas="upsell-' + u.id + '" data-lp-upsell-shape="' + thumbShape + '" width="' + cw + '" height="' + ch + '"></canvas></span>'
        + '<span class="arlo-lp__upsell-text">'
        +   '<span class="arlo-lp__upsell-title">' + (u.title || 'Add-on') + '</span>'
        +   '<span class="arlo-lp__upsell-desc">' + (u.description || '') + '</span>'
        + '</span>'
        + '<span class="arlo-lp__upsell-price">' + was + '$' + Number(u.discountedPrice || u.fullPrice || 0).toFixed(2) + '</span>'
        + '</div>';
    }).join('');
    box.dataset.built = '1';
    // freshly created canvases start blank — paint them
    schedulePreview();
  }

  function updateAtcState() {
    const nameOk = el('arlo-lp-first').value.trim().length > 0;
    const packOk = state.packIdx !== null && data.variants[state.packIdx];
    if (STANDALONE) {
      const b0 = el('arlo-lp-atc');
      const okS = nameOk && packOk;
      b0.disabled = !okS;
      b0.textContent = okS ? 'Add to cart' : (!nameOk ? 'Enter a name to continue' : 'Choose an option');
      return;
    }
    const ready = nameOk && packOk;
    const b = el('arlo-lp-atc');
    b.disabled = !ready;
    b.textContent = ready
      ? 'Add to cart'
      : (!nameOk ? 'Enter a name to continue' : 'Choose a pack to continue');
  }

  function updateCounters() {
    const max = cfg.maxChars || 12;
    ['first', 'last'].forEach((k) => {
      const input = el('arlo-lp-' + k);
      if (input.value.length > max) input.value = input.value.slice(0, max);
      el('arlo-lp-' + k + '-count').textContent = input.value.length + '/' + max;
    });
    el('arlo-lp-long-warning').style.display =
      el('arlo-lp-first').value.length > (cfg.longNameWarnAt || 9) ? 'block' : 'none';
  }

  /* ── Add to Cart: PDFs → Uploadcare → /cart/add.js (separate lines) ───── */
  let _atcBusy = false;
  async function addToCart() {
    if (_atcBusy) return;
    const first = el('arlo-lp-first').value.trim();
    const last = el('arlo-lp-last').value.trim();
    if (!first || state.packIdx === null) return;
    const pack = STANDALONE ? null : cfg.packs[state.packIdx];
    const variant = data.variants[state.packIdx];
    if (!variant || (!STANDALONE && !pack)) return;
    _atcBusy = true;

    const overlay = el('arlo-lp-status');
    const statusText = el('arlo-lp-status-text');
    const setStatus = (msg) => { statusText.textContent = msg; };
    overlay.style.display = 'flex';

    try {
      const colour = currentColour();
      const font = currentFont();
      // Preload per-shape design images once
      const imgs = {};
      for (const sh of ['rect', 'circle', 'pencil', 'bagtag']) {
        imgs[sh] = await loadImage(imgUrlFor(sh));
      }
      // Sheet labels + previews use the picked font colour. The bag tag (whether
      // standalone or a matching add-on) overrides colourHex per compose call
      // with its background's white/black — see bagtagBaseOpts().
      const baseOpts = {
        first, last,
        colourHex: colour.hex,
        font,
        dpi: (cfg.sheet && cfg.sheet.dpi) || 300,
        imgFor: (sh) => imgs[sh],
        bagtagCfg: cfg.bagtag,
        bagtagFont: bagtagFontCfg(),
      };

      const sharedProps = {
        // _image drives the cart drawer thumbnail (image 2 = a real product
        // photo; image 1 is the flat label background)
        '_image': data.cartImage || '',
        'Name': [first, last].filter(Boolean).join(' '),
        'Font': font.label,
        'Colour': colour.name,
        'Design': data.title,
      };

      const items = [];

      // Standalone product: one line item, its own PDFs, no pack/add-on logic
      if (STANDALONE) {
        const props = Object.assign({ 'Item': data.title }, sharedProps);
        if (STANDALONE.kind === 'bagtag') {
          if (bagtagUsesLibrary()) {
            const bg = currentBagBg();
            if (bg && bg.name) props['Background'] = bg.name;
            props['Colour'] = bagtagUsesLabelStyle()
              ? currentColour().name
              : ((bgTextHex(bg) === '#ffffff') ? 'White text' : 'Black text');
          }
          const blob = await composeBagTagPdf(Object.assign({}, baseOpts, { colourHex: textHexFor('bagtag'), bagtagPlate: bagtagUsesLabelStyle(), bagtagFullArtwork: !!effectiveBagBg() }), setStatus);
          setStatus('Uploading artwork…');
          props['_label_pdf_1'] = await uploadToUploadcare(blob, 'bagtag-' + data.handle + '.pdf');
        } else {
          const blobs = await composeSheetPdfs(
            STANDALONE.contents || [], Object.assign({}, baseOpts, { mirror: !!STANDALONE.mirror }), setStatus);
          for (let i = 0; i < blobs.length; i++) {
            setStatus('Uploading artwork ' + (i + 1) + '/' + blobs.length + '…');
            props['_label_pdf_' + (i + 1)] = await uploadToUploadcare(
              blobs[i], data.handle + (blobs.length > 1 ? '-p' + (i + 1) : '') + '.pdf');
          }
        }
        items.push({ id: variant.id, quantity: 1, properties: props });
        setStatus('Adding to cart…');
        const res0 = await fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res0.ok) {
          const err0 = await res0.json().catch(() => ({}));
          throw new Error(err0.description || 'Could not add to cart');
        }
        try { sessionStorage.setItem('arlo_open_cart', '1'); } catch (e) {}
        window.location.reload();
        return;
      }

      const outputs = [
        { key: 'main', mirror: false, variantId: variant.id, note: pack.name },
        { key: 'iron', mirror: true, variantId: cfg.linkedProducts && cfg.linkedProducts.ironVariantId, note: 'Iron-On — ' + pack.name },
        { key: 'bagtag', mirror: false, variantId: cfg.linkedProducts && cfg.linkedProducts.bagtagVariantId, note: 'Bag Tag — ' + pack.name },
      ];

      for (const out of outputs) {
        const contents = (pack.contents || []).filter((c) => (c.output || 'main') === out.key && c.qty > 0);
        if (out.key !== 'main' && !contents.length) continue;
        if (!out.variantId) {
          if (out.key === 'main') throw new Error('Pack variant missing');
          console.warn('[ArloLabels] no linked variant for ' + out.key + ' — skipping line item');
          continue;
        }
        let blobs;
        if (out.key === 'bagtag') {
          blobs = [await composeBagTagPdf(Object.assign({}, baseOpts, { colourHex: textHexFor('bagtag'), bagtagPlate: bagtagUsesLabelStyle(), bagtagFullArtwork: !!effectiveBagBg() }), setStatus)];
        } else {
          blobs = await composeSheetPdfs(contents, Object.assign({}, baseOpts, { mirror: out.mirror }), setStatus);
        }
        const props = Object.assign({ 'Pack': out.note }, sharedProps);
        if (out.key === 'bagtag') {
          const mb = matchedBagBg();
          if (mb && mb.name) props['Background'] = mb.name;
        }
        for (let i = 0; i < blobs.length; i++) {
          setStatus('Uploading artwork ' + (i + 1) + '/' + blobs.length + '…');
          const fname = 'labels-' + data.handle + '-' + out.key + (blobs.length > 1 ? '-p' + (i + 1) : '') + '.pdf';
          props['_label_pdf_' + (i + 1)] = await uploadToUploadcare(blobs[i], fname);
        }
        items.push({ id: parseInt(out.variantId, 10), quantity: 1, properties: props });
      }

      // Optional paid add-ons at their real product price. The saving is
      // applied by a native Shopify automatic Buy X Get Y discount at
      // checkout, so no discount code is used here.
      for (const u of activeUpsells()) {
        if (!state.upsells[u.id]) continue;
        setStatus('Preparing ' + (u.title || 'add-on').toLowerCase() + '…');
        const props = Object.assign({ 'Pack': u.title || 'Add-on' }, sharedProps);
        if (u.kind === 'bagtag') {
          const mb = matchedBagBg();
          if (mb && mb.name) props['Background'] = mb.name;
          const blob = await composeBagTagPdf(Object.assign({}, baseOpts, { colourHex: textHexFor('bagtag'), bagtagPlate: bagtagUsesLabelStyle(), bagtagFullArtwork: !!effectiveBagBg() }), setStatus);
          setStatus('Uploading artwork…');
          props['_label_pdf_1'] = await uploadToUploadcare(blob, 'bagtag-' + data.handle + '.pdf');
        } else {
          const blobs = await composeSheetPdfs(
            u.contents || [], Object.assign({}, baseOpts, { mirror: !!u.mirror }), setStatus);
          for (let i = 0; i < blobs.length; i++) {
            setStatus('Uploading artwork ' + (i + 1) + '/' + blobs.length + '…');
            props['_label_pdf_' + (i + 1)] = await uploadToUploadcare(
              blobs[i], u.id + '-' + data.handle + (blobs.length > 1 ? '-p' + (i + 1) : '') + '.pdf');
          }
        }
        items.push({ id: parseInt(u.variantId, 10), quantity: 1, properties: props });
      }

      setStatus('Adding to cart…');
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart');
      }

      // Reload with the drawer auto-open flag (same pattern as main-cart)
      try { sessionStorage.setItem('arlo_open_cart', '1'); } catch (e) {}
      window.location.reload();
    } catch (e) {
      console.error('[ArloLabels] add to cart failed', e);
      overlay.style.display = 'none';
      const errEl = el('arlo-lp-atc-error');
      errEl.textContent = 'Sorry, something went wrong preparing your labels — please try again.';
      errEl.style.display = 'block';
      _atcBusy = false;
    }
  }

  /* ── Events ───────────────────────────────────────────────────────────── */
  root.addEventListener('click', (e) => {
    const shapeBtn = e.target.closest('[data-lp-shape]');
    if (shapeBtn) { setShape(shapeBtn.dataset.lpShape); return; }
    const bgBtn = e.target.closest('[data-lp-bg]');
    if (bgBtn) {
      state.bgIdx = parseInt(bgBtn.dataset.lpBg, 10);
      buildControls();
      snapToPreview();
      schedulePreview();
      return;
    }
    const swatch = e.target.closest('[data-lp-colour]');
    if (swatch) {
      state.colourIdx = parseInt(swatch.dataset.lpColour, 10);
      buildControls();
      snapToPreview();
      schedulePreview();
      return;
    }
    const fontBtn = e.target.closest('[data-lp-font]');
    if (fontBtn) {
      state.fontIdx = parseInt(fontBtn.dataset.lpFont, 10);
      buildControls();
      snapToPreview();
      schedulePreview();
      return;
    }
    const help = e.target.closest('[data-lp-help]');
    if (help) {
      help.closest('.arlo-lp__pack').classList.toggle('help-open');
      e.stopPropagation();
      return;
    }
    const upsellEl = e.target.closest('[data-lp-upsell]');
    if (upsellEl) {
      const id = upsellEl.dataset.lpUpsell;
      state.upsells[id] = !state.upsells[id];
      buildUpsell();
      schedulePreview();
      return;
    }
    const packEl = e.target.closest('[data-lp-pack]');
    if (packEl) {
      state.packIdx = parseInt(packEl.dataset.lpPack, 10);
      buildControls();
      updateAtcState();
      snapToPreview();
      return;
    }
  });

  ['arlo-lp-first', 'arlo-lp-last'].forEach((id) => {
    el(id).addEventListener('input', () => {
      updateCounters();
      updateAtcState();
      snapToPreview();
      schedulePreview();
    });
    el(id).addEventListener('focus', snapToPreview);
  });

  el('arlo-lp-atc').addEventListener('click', addToCart);

  // Swipe / drag through the gallery slides (previewer first, then photos).
  // Pointer events cover touch, pen and mouse-drag; vertical movement is left
  // to the page so normal scrolling still works.
  (function () {
    const media = el('arlo-lp-media');
    if (!media) return;
    let startX = null, startY = null, dragging = false;

    const slideKeys = () => Array.prototype.map.call(
      root.querySelectorAll('[data-lp-slide]'), (sl) => sl.dataset.lpSlide);

    function step(dir) {
      const keys = slideKeys();
      const cur = root.querySelector('.arlo-lp__slide.active');
      const idx = keys.indexOf(cur ? cur.dataset.lpSlide : 'preview');
      const next = Math.min(Math.max(idx + dir, 0), keys.length - 1);
      if (next !== idx) showSlide(keys[next]);
    }

    function onDown(e) {
      if (e.target.closest('[data-lp-shape]')) return; // let the pills work
      startX = e.clientX; startY = e.clientY; dragging = true;
    }
    function onUp(e) {
      if (!dragging) return;
      dragging = false;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      startX = startY = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return; // vertical scroll
      step(dx < 0 ? 1 : -1);
    }

    if (window.PointerEvent) {
      media.addEventListener('pointerdown', onDown);
      media.addEventListener('pointerup', onUp);
      media.addEventListener('pointercancel', () => { dragging = false; });
    } else {
      media.addEventListener('touchstart', (e) => onDown(e.touches[0]), { passive: true });
      media.addEventListener('touchend', (e) => onUp(e.changedTouches[0]), { passive: true });
    }
    // keyboard support for accessibility
    media.setAttribute('tabindex', '0');
    media.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { step(1); e.preventDefault(); }
      if (e.key === 'ArrowLeft') { step(-1); e.preventDefault(); }
    });
  })();

  /* ── Gallery: slide 1 is the live previewer, slides 2+ are product photos.
     Any personalisation action snaps back to the previewer. ─────────────── */
  function showSlide(key) {
    root.querySelectorAll('[data-lp-slide]').forEach((sl) => {
      sl.classList.toggle('active', sl.dataset.lpSlide === String(key));
    });
    root.querySelectorAll('[data-lp-dot]').forEach((d) => {
      d.classList.toggle('active', d.dataset.lpDot === String(key));
    });
  }
  function snapToPreview() {
    const cur = root.querySelector('.arlo-lp__slide--preview');
    if (cur && cur.classList.contains('active')) return;
    showSlide('preview');
  }

  (function () {
    const dots = el('arlo-lp-dots');
    if (!dots) return;
    // only meaningful when there's more than one slide
    if (root.querySelectorAll('[data-lp-slide]').length < 2) { dots.style.display = 'none'; }
    dots.addEventListener('click', (e) => {
      const d = e.target.closest('[data-lp-dot]');
      if (d) showSlide(d.dataset.lpDot);
    });
  })();

  let _resizeTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(fitAllCanvases, 120);
  });

  /* ── Init ─────────────────────────────────────────────────────────────── */
  buildControls();
  updateCounters();
  updateAtcState();
  setShape(PREVIEW_SHAPES[0]);
  fitAllCanvases();
  schedulePreview();
})();
